import type React from "react";
import {
  FluentProvider,
  createLightTheme,
  createDarkTheme,
  makeStaticStyles,
  tokens,
  type BrandVariants,
} from "@fluentui/react-components";
import { useAtomValue } from "jotai";
import { darkModeAtom, isRTLAtom } from "../store";

// Datatorque brand — primary Green #94D600 (source: Sandbox/branding-guidelines/brand/colours.json)
const brandTheme: BrandVariants = {
  10: "#131C00",
  20: "#263B00",
  30: "#3B5A00",
  40: "#527F00",
  50: "#94D600",
  60: "#A6E11F",
  70: "#B6EA3E",
  80: "#94D600",
  90: "#C4EE5D",
  100: "#D2F27C",
  110: "#DEF69B",
  120: "#E8F8B4",
  130: "#EFFACC",
  140: "#F5FBDD",
  150: "#F9FCE9",
  160: "#FBFDF2",
};

// Shell chrome shared with the officer app (Frontend/src/styles/ClassNames.ts): the navy header,
// the rail widths and the borders. The brand ramp colours controls; the chrome stays the suite's.
const useShellVariables = makeStaticStyles([
  `.fui-FluentProvider {
    --app-header-height: 48px;
    --app-header-btn-width: 48px;
    --app-header-text: ${tokens.colorNeutralForegroundInverted2};
    --app-header-bg: #002943;
    --app-header-border-bottom-width: 1px;
    --app-header-border-bottom-color: var(--app-header-bg);
    --app-header-text-dark: ${tokens.colorNeutralForeground1};
    --app-header-bg-dark: ${tokens.colorNeutralBackground3};
    --app-header-border-bottom-color-dark: ${tokens.colorNeutralStroke1};
    --app-sidenav-width: 280px;
    --app-sidenav-collapsed-width: 56px;
    --app-sidenav-border-right-width: 1px;
    --app-sidenav-border-right-color: ${tokens.colorNeutralStroke2};
  }`,
]);

const lightTheme = createLightTheme(brandTheme);
const darkTheme = createDarkTheme(brandTheme);

export const ThemeProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const darkMode = useAtomValue(darkModeAtom);
  const isRTL = useAtomValue(isRTLAtom);

  const theme = darkMode ? darkTheme : lightTheme;
  useShellVariables();

  return (
    <FluentProvider theme={theme} dir={isRTL ? "rtl" : "ltr"}>
      {children}
    </FluentProvider>
  );
};
